import type { PublicDocumentSession } from "@emr/contracts";
import { type FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { DateOfBirthForm, PatientTopBar, StateMark } from "../intake/PatientChrome";
import {
  type PublicDocumentLinkLocation,
  takePublicPatientLink,
} from "../intake/publicPatientLink";
import {
  DocumentLinkUnavailableError,
  documentClinicUnreachable,
  documentLinkUnavailable,
  openPublicDocument,
  readPublicDocument,
} from "./publicDocumentClient";

function clinicDate(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(new Date(value));
}

/** One sent PDF, held in memory only. The download tap is a real link with no asynchronous work. */
export function PublicDocumentLink({
  link,
  idleTimeoutMs = 10 * 60_000,
}: {
  readonly link: PublicDocumentLinkLocation;
  readonly idleTimeoutMs?: number;
}) {
  const token = useRef(link.token);
  const operation = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const blobUrl = useRef<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [session, setSession] = useState<PublicDocumentSession | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [closed, setClosed] = useState(link.token === null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clear = useCallback(() => {
    generation.current += 1;
    operation.current?.abort();
    operation.current = null;
    token.current = null;
    if (blobUrl.current !== null) URL.revokeObjectURL(blobUrl.current);
    blobUrl.current = null;
  }, []);
  const close = useCallback(() => {
    clear();
    setDateOfBirth("");
    setSession(null);
    setDownloadUrl(null);
    setBusy(false);
    setError(null);
    setClosed(true);
  }, [clear]);

  useEffect(() => {
    document.title = "Secure document link · Bob Doc";
    let timeout: ReturnType<typeof setTimeout>;
    const activity = () => {
      clearTimeout(timeout);
      timeout = setTimeout(close, idleTimeoutMs);
    };
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) close();
    };
    activity();
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    window.addEventListener("pageshow", restored);
    // Do not revoke on pagehide: Safari may fire it while handing a Blob download to the user.
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
      window.removeEventListener("pageshow", restored);
      // StrictMode replays this effect before interaction. Keep the untouched link for its replay.
      generation.current += 1;
      operation.current?.abort();
      operation.current = null;
      if (blobUrl.current !== null) URL.revokeObjectURL(blobUrl.current);
      blobUrl.current = null;
    };
  }, [close, idleTimeoutMs]);

  // A layout effect: the document opens asynchronously, and focus must move in the same commit
  // that shows the new heading.
  useLayoutEffect(() => {
    if (closed || session !== null) heading.current?.focus();
  }, [closed, session]);
  useEffect(() => {
    const takeNewLink = () => {
      const next = takePublicPatientLink();
      if (
        next?.purpose !== "document" ||
        next.clinicSlug !== link.clinicSlug ||
        next.token === null ||
        session !== null ||
        operation.current !== null
      )
        return;
      token.current = next.token;
      setError(null);
      setDateOfBirth("");
      setClosed(false);
    };
    window.addEventListener("hashchange", takeNewLink);
    return () => window.removeEventListener("hashchange", takeNewLink);
  }, [link.clinicSlug, session]);

  const open = async (event: FormEvent) => {
    event.preventDefault();
    if (token.current === null || operation.current !== null || closed) return;
    const controller = new AbortController();
    operation.current = controller;
    const current = generation.current;
    const input = { token: token.current, dateOfBirth };
    const isCurrent = () => generation.current === current && !controller.signal.aborted;
    setBusy(true);
    setError(null);
    try {
      const opened = await openPublicDocument(link.clinicSlug, input, controller.signal);
      if (!isCurrent()) return;
      const pdf = await readPublicDocument(
        link.clinicSlug,
        input,
        opened.byteSize,
        controller.signal,
      );
      if (!isCurrent()) return;
      const url = URL.createObjectURL(pdf);
      blobUrl.current = url;
      setDownloadUrl(url);
      setSession(opened);
      setDateOfBirth("");
      token.current = null;
    } catch (caught) {
      if (isCurrent())
        setError(
          caught instanceof DocumentLinkUnavailableError
            ? documentLinkUnavailable
            : documentClinicUnreachable,
        );
    } finally {
      if (operation.current === controller) operation.current = null;
      if (isCurrent()) setBusy(false);
    }
  };

  const ready = !closed && session !== null && downloadUrl !== null;
  return (
    <div className="patient-app">
      <PatientTopBar
        label="Secure document"
        clinicName={session?.clinicName}
        onEndSession={closed ? undefined : close}
      />
      <main className="intake-state">
        <StateMark icon={closed ? "lock" : "document"} />
        <h1 ref={heading} tabIndex={-1}>
          {closed ? "This session is closed" : (session?.title ?? "Open your document")}
        </h1>
        {closed ? (
          <p>Reopen the original link or ask the clinic for a new one.</p>
        ) : ready ? (
          // The top bar names the clinic ("for …"), so the card does not repeat it.
          <section className="document-ready" aria-label="Document details">
            <dl className="document-details">
              <div>
                <dt>Issued</dt>
                <dd>{clinicDate(session.issuedAt, session.timeZone)}</dd>
              </div>
              <div>
                <dt>PDF</dt>
                <dd>
                  {session.pageCount} {session.pageCount === 1 ? "page" : "pages"} ·{" "}
                  {Math.max(1, Math.ceil(session.byteSize / 1024))} KB
                </dd>
              </div>
              <div>
                <dt>Link expires</dt>
                <dd>{clinicDate(session.expiresAt, session.timeZone)}</dd>
              </div>
            </dl>
            <a
              className="continue-button document-download"
              href={downloadUrl}
              download={session.fileName}
            >
              Download PDF
            </a>
            <p className="document-note">
              Keep it private on a shared device.
              {session.clinicPhone !== null ? ` Questions? Call ${session.clinicPhone}.` : null}
            </p>
          </section>
        ) : (
          <>
            <p>For your privacy, confirm your date of birth.</p>
            {/* A refusal only comes while the link is opening (closing clears it), so it shows
                in the form, under the field. */}
            <DateOfBirthForm
              value={dateOfBirth}
              onChange={setDateOfBirth}
              onSubmit={(event) => void open(event)}
              busy={busy}
              error={error}
            />
          </>
        )}
      </main>
    </div>
  );
}
